import { useState } from 'react';

import { AppIcon } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { QuizResultModel } from '@backrs_ui/grow-ui/src/pages/SatActBooster/utils/quizScoring';

import { Box, Button, Collapse, SxProps, Theme, Typography } from '@mui/material';

import { QuestionMarkdown, markdownContentSx } from './QuestionMarkdown';
import { QuizFigure } from './QuizFigure';

const STRINGS = appStrings.modules.satActBooster.dailyPractice.results;
const QUIZ_STRINGS = appStrings.modules.satActBooster.dailyPractice.quiz;
const FEEDBACK_STRINGS = appStrings.modules.satActBooster.dailyPractice.feedback;

type QuizResultsProps = Readonly<{
  model: QuizResultModel;
  /** Moves to the next day holding a quiz; omitted when there is no next one to offer. */
  onNextQuiz?: () => void;
}>;

/**
 * End-of-quiz results: score, a by-skill breakdown, an expanded review, and a forward CTA. Pure
 * presentation over the resolved model — the by-skill breakdown and review appear only when
 * per-question detail is available (an older attempt saved before answers were recorded shows the
 * score alone).
 */
export const QuizResults = ({ model, onNextQuiz }: QuizResultsProps) => {
  const [reviewOpen, setReviewOpen] = useState(true);
  const hasDetail = model.reviewItems.length > 0;

  return (
    <Box sx={cardSx} id="booster-quiz-results">
      <Box sx={scoreColSx}>
        <Typography variant="text-sm-semibold" sx={titleSx}>
          {STRINGS.title}
        </Typography>
        <Box sx={scoreRowSx}>
          <Typography variant="display-sm-semibold" sx={scoreSx}>
            {STRINGS.score(model.score, model.total)}
          </Typography>
          <Typography variant="text-md-medium" sx={percentSx}>
            {STRINGS.percent(model.percent)}
          </Typography>
        </Box>
      </Box>

      {hasDetail && (
        <Box sx={breakdownSx}>
          <Typography variant="text-sm-semibold" sx={breakdownTitleSx}>
            {STRINGS.bySkillTitle}
          </Typography>
          {model.bySkill.map((entry) => (
            <Box key={entry.skill} sx={breakdownRowSx}>
              <Typography variant="text-sm-regular" sx={skillNameSx}>
                {entry.skill}
              </Typography>
              <Typography variant="text-sm-semibold" sx={skillScoreSx}>
                {STRINGS.score(entry.correct, entry.total)}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {hasDetail && (
        <Box>
          <Button
            id="booster-quiz-review-toggle"
            onClick={() => setReviewOpen((open) => !open)}
            sx={reviewToggleSx}
            endIcon={
              <AppIcon
                iconName={reviewOpen ? 'ChevronUp' : 'ChevronDown'}
                diameter="16px"
                color="currentColor"
              />
            }
          >
            {STRINGS.reviewToggle}
          </Button>
          <Collapse in={reviewOpen}>
            <Box sx={reviewListSx}>
              {model.reviewItems.map((item) => {
                const hasOptionDetail = item.options.some((option) => option.explanation);

                return (
                  <Box key={item.id} sx={reviewItemSx}>
                    <Typography variant="text-sm-medium" component="div" sx={reviewStemSx}>
                      <QuestionMarkdown content={item.stem} size="SMALL" />
                    </Typography>
                    <Typography variant="text-sm-semibold" sx={reviewAnswerSx(item.correct)}>
                      {STRINGS.answerLine(item.yourAnswer, item.correctAnswer)}
                    </Typography>
                    {item.options.map((option) => (
                      <Box key={option.id} sx={reviewOptionSx(option.isCorrect)}>
                        <Box sx={reviewOptionHeadSx}>
                          <Typography
                            variant="text-sm-medium"
                            component="span"
                            sx={reviewOptionLetterSx}
                          >
                            {QUIZ_STRINGS.optionLabel(option.letter)}
                          </Typography>
                          <Typography
                            variant="text-sm-medium"
                            component="div"
                            sx={reviewOptionTextSx}
                          >
                            <QuestionMarkdown content={option.text} size="SMALL" />
                            <QuizFigure
                              url={option.mediaUrl}
                              size="SMALL"
                              alt={QUIZ_STRINGS.optionFigureAlt(option.letter)}
                            />
                          </Typography>
                        </Box>
                        {option.explanation && (
                          <Typography
                            variant="text-sm-regular"
                            component="div"
                            sx={reviewOptionReasonSx}
                          >
                            <QuestionMarkdown content={option.explanation} size="SMALL" />
                          </Typography>
                        )}
                      </Box>
                    ))}
                    {item.explanation && (
                      <Box sx={reviewOverallColSx}>
                        {hasOptionDetail && (
                          <Typography variant="text-sm-semibold" sx={reviewOverallTitleSx}>
                            {FEEDBACK_STRINGS.overallTitle}
                          </Typography>
                        )}
                        <Typography
                          variant="text-sm-regular"
                          component="div"
                          sx={reviewExplanationSx}
                        >
                          <QuestionMarkdown content={item.explanation} size="SMALL" />
                        </Typography>
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Collapse>
        </Box>
      )}

      {onNextQuiz && (
        <Button
          id="booster-quiz-next-day-btn"
          variant="contained"
          size="lg"
          hierarchy="Primary"
          onClick={onNextQuiz}
          sx={nextQuizButtonSx}
        >
          {STRINGS.takeNextQuiz}
        </Button>
      )}
    </Box>
  );
};

const cardSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-2xl'],
  padding: backrs.spacing['spacing-2xl'],
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  border: `1px solid ${backrs.colors['Beige/950']}`,
  borderRadius: backrs.radius['radius-xl'],
  boxShadow: backrs.shadows['shadow-xs'],
});

const scoreColSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
});

const titleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const scoreRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'baseline',
  gap: backrs.spacing['spacing-md'],
});

const scoreSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const percentSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-brand-secondary'],
});

const breakdownSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-sm'],
});

const breakdownTitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const breakdownRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: backrs.spacing['spacing-md'],
  paddingBlock: backrs.spacing['spacing-xs'],
  borderBottom: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
});

const skillNameSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
});

const skillScoreSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
  whiteSpace: 'nowrap',
});

const reviewToggleSx: SxProps<Theme> = ({ backrs, typography }) => ({
  ...typography['text-sm-semibold'],
  textTransform: 'none',
  padding: 0,
  minWidth: 0,
  color: backrs.colors['Colors/Text/text-brand-secondary'],
  '&:hover': { backgroundColor: 'transparent' },
});

const reviewListSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
  paddingTop: backrs.spacing['spacing-lg'],
});

const reviewItemSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
});

const reviewStemSx: SxProps<Theme> = ({ backrs }) => ({
  ...markdownContentSx,
  color: backrs.colors['Colors/Text/text-primary'],
});

const reviewOptionSx =
  (isCorrect: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: backrs.spacing['spacing-xxs'],
    marginLeft: backrs.spacing['spacing-lg'],
    paddingInline: backrs.spacing['spacing-md'],
    paddingBlock: backrs.spacing['spacing-xs'],
    borderLeft: `2px solid ${
      isCorrect
        ? backrs.colors['Colors/Foreground/fg-success-primary']
        : backrs.colors['Colors/Border/border-secondary']
    }`,
  });

const reviewOptionHeadSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'baseline',
  gap: backrs.spacing['spacing-xs'],
});

const reviewOptionLetterSx: SxProps<Theme> = ({ backrs }) => ({
  flexShrink: 0,
  color: backrs.colors['Colors/Text/text-secondary'],
});

// The letter is a sibling, so this column stays block flow — a figure has to stack under the text.
const reviewOptionTextSx: SxProps<Theme> = ({ backrs }) => ({
  ...markdownContentSx,
  flex: 1,
  minWidth: 0,
  color: backrs.colors['Colors/Text/text-primary'],
});

const reviewOptionReasonSx: SxProps<Theme> = ({ backrs }) => ({
  ...markdownContentSx,
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const reviewAnswerSx =
  (correct: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    color: correct
      ? backrs.colors['Colors/Text/text-success-primary']
      : backrs.colors['Colors/Text/text-error-primary'],
  });

const reviewOverallColSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xxs'],
});

const reviewOverallTitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
});

const reviewExplanationSx: SxProps<Theme> = ({ backrs }) => ({
  ...markdownContentSx,
  color: backrs.colors['Colors/Text/text-tertiary'],
});

// The card is a stretch column, so the button has to opt out of filling its width.
const nextQuizButtonSx: SxProps<Theme> = {
  alignSelf: 'flex-start',
};
